"use client"

import { use$ } from "@legendapp/state/react"
import { usePostHog } from "posthog-js/react"
import { useCallback } from "react"
import { buildAuthenticatedPostHogEventContextProperties, getAuthenticatedPostHogGroupKey } from "@/lib/posthog/identity"
import type { TUserStatus } from "@/schemas/user.schema"
import { syncStore$ } from "@/stores/sync.v2.store"

export interface TrackEngagementParams {
  engagement_type: "call" | "sms" | "email"
  direction: "outbound" | "inbound"
  call_source?: "sequence" | "powerdialer" | "c2c" | "manual"
}

type TrackingUser = Pick<TUserStatus, "accountId" | "companyName">

export function getTrackingCompanyGroupKey(user?: TrackingUser | null) {
  return user ? getAuthenticatedPostHogGroupKey(user) : null
}

export function withTrackingCompanyGroup<TProperties extends Record<string, unknown>>(properties: TProperties, user?: TrackingUser | null) {
  const accountProperties = user ? buildAuthenticatedPostHogEventContextProperties(user) : {}

  if (Object.keys(accountProperties).length === 0) {
    return properties
  }

  return {
    ...properties,
    ...accountProperties,
  }
}

export function useEngagementTracking() {
  const posthog = usePostHog()
  const user = use$(syncStore$.userStatus)

  const trackEngagement = useCallback(
    (params: TrackEngagementParams) => {
      try {
        // Only include call_source for call engagements
        const eventParams: Record<string, unknown> = {
          engagement_type: params.engagement_type,
          direction: params.direction,
        }

        if (params.engagement_type === "call" && params.call_source) {
          eventParams.call_source = params.call_source
        }

        posthog.capture("user_created_engagement", withTrackingCompanyGroup(eventParams, user))
      } catch (error) {
        // Silently fail to avoid disrupting core functionality
        console.warn("Failed to track engagement:", error)
      }
    },
    [posthog, user]
  )

  const trackFeatureUsage = useCallback(
    (
      featureName:
        | "powerdialer"
        | "fileupload"
        | "addtopowerdialer"
        | "setringlocal"
        | "vmdropped"
        | "dispositionedcall"
        | "nextaction"
        | "addnote"
        | "adddnote"
        | "statschatbot_opened"
        | "statschatbot_closed"
        | "statschatbot_message_sent"
        | "statschatbot_cleared"
        | "statschatbot_chart_rendered"
    ) => {
      try {
        posthog.capture(
          "user_used_feature",
          withTrackingCompanyGroup(
            {
              feature_name: featureName,
            },
            user
          )
        )
      } catch (error) {
        // Silently fail to avoid disrupting core functionality
        console.warn("Failed to track feature usage:", error)
      }
    },
    [posthog, user]
  )

  const trackActivation = useCallback(
    (
      activationAction:
        | "inviteduser"
        | "connectedcrm"
        | "connectedemail"
        | "installedextension"
        | "call_joe_test"
        | "call_a_friend"
        | "call_a_lead_from_crm"
        | "practice_power_dialing_with_bots"
        | "upload_contacts_list"
        | "try_prebuilt_sales_sequence"
        | "view_daily_stats"
    ) => {
      try {
        posthog.capture(
          "user_activated",
          withTrackingCompanyGroup(
            {
              activation_action: activationAction,
            },
            user
          )
        )
      } catch (error) {
        // Silently fail to avoid disrupting core functionality
        console.warn("Failed to track activation:", error)
      }
    },
    [posthog, user]
  )

  const trackLifecycle = useCallback(
    (lifecycleEvent: "trialexpired" | "subscription_peek" | "subscribed" | "canceled") => {
      try {
        posthog.capture(
          "user_changed_account_lifecycle",
          withTrackingCompanyGroup(
            {
              lifecycle_event: lifecycleEvent,
            },
            user
          )
        )
      } catch (error) {
        // Silently fail to avoid disrupting core functionality
        console.warn("Failed to track lifecycle:", error)
      }
    },
    [posthog, user]
  )

  return { trackEngagement, trackFeatureUsage, trackActivation, trackLifecycle }
}
